// ===========================================
// 🔧 UTILIDADES FINANCIERAS MEJORADAS
// ===========================================

// Función para formatear números con separadores según la moneda
export const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '';
  
  const currencyConfig = {
    'USD': { locale: 'en-US', currency: 'USD' },
    'EUR': { locale: 'de-DE', currency: 'EUR' },
    'CLP': { locale: 'es-CL', currency: 'CLP' },
    'MXN': { locale: 'es-MX', currency: 'MXN' },
    'ARS': { locale: 'es-AR', currency: 'ARS' },
    'COP': { locale: 'es-CO', currency: 'COP' }
  };

  const config = currencyConfig[currency] || currencyConfig['USD'];
  
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Función para formatear solo números (sin símbolo de moneda)
export const formatNumber = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '';
  
  const localeMap = {
    'USD': 'en-US',
    'EUR': 'de-DE', 
    'CLP': 'es-CL',
    'MXN': 'es-MX',
    'ARS': 'es-AR',
    'COP': 'es-CO'
  };

  const locale = localeMap[currency] || 'en-US';
  
  return new Intl.NumberFormat(locale).format(amount);
};

// Opciones de tiempo más flexibles - función que acepta t() para i18n
export const getTimeframeOptions = (t) => [
  // Corto plazo
  { value: '3_months', label: t('agtfin.utils.financial.timeframes.3_months'), months: 3, category: 'short' },
  { value: '6_months', label: t('agtfin.utils.financial.timeframes.6_months'), months: 6, category: 'short' },
  { value: '9_months', label: t('agtfin.utils.financial.timeframes.9_months'), months: 9, category: 'short' },

  // Mediano plazo
  { value: '1_year', label: t('agtfin.utils.financial.timeframes.1_year'), months: 12, category: 'medium' },
  { value: '18_months', label: t('agtfin.utils.financial.timeframes.18_months'), months: 18, category: 'medium' },
  { value: '2_years', label: t('agtfin.utils.financial.timeframes.2_years'), months: 24, category: 'medium' },
  { value: '30_months', label: t('agtfin.utils.financial.timeframes.30_months'), months: 30, category: 'medium' },
  { value: '3_years', label: t('agtfin.utils.financial.timeframes.3_years'), months: 36, category: 'medium' },

  // Largo plazo
  { value: '4_years', label: t('agtfin.utils.financial.timeframes.4_years'), months: 48, category: 'long' },
  { value: '5_years', label: t('agtfin.utils.financial.timeframes.5_years'), months: 60, category: 'long' },
  { value: '7_years', label: t('agtfin.utils.financial.timeframes.7_years'), months: 84, category: 'long' },
  { value: '10_years', label: t('agtfin.utils.financial.timeframes.10_years'), months: 120, category: 'long' },

  // Personalizado
  { value: 'custom', label: t('agtfin.utils.financial.timeframes.custom'), months: 0, category: 'custom' }
];

// Backward compatibility: export estático (deprecated)
export const timeframeOptions = [
  { value: '3_months', label: '3 meses', months: 3, category: 'short' },
  { value: '6_months', label: '6 meses', months: 6, category: 'short' },
  { value: '9_months', label: '9 meses', months: 9, category: 'short' },
  { value: '1_year', label: '1 año', months: 12, category: 'medium' },
  { value: '18_months', label: '1 año y medio', months: 18, category: 'medium' },
  { value: '2_years', label: '2 años', months: 24, category: 'medium' },
  { value: '30_months', label: '2 años y medio', months: 30, category: 'medium' },
  { value: '3_years', label: '3 años', months: 36, category: 'medium' },
  { value: '4_years', label: '4 años', months: 48, category: 'long' },
  { value: '5_years', label: '5 años', months: 60, category: 'long' },
  { value: '7_years', label: '7 años', months: 84, category: 'long' },
  { value: '10_years', label: '10 años', months: 120, category: 'long' },
  { value: 'custom', label: 'Fecha personalizada', months: 0, category: 'custom' }
];

// Obtener opciones de tiempo según el tipo de meta
export const getTimeframesByGoal = (goalId) => {
  const goalTimeframes = {
    'buy_house': ['1_year', '2_years', '3_years', '4_years', '5_years', '7_years', '10_years', 'custom'],
    'buy_car': ['6_months', '1_year', '18_months', '2_years', '3_years', 'custom'],
    'education': ['6_months', '1_year', '2_years', '3_years', '4_years', 'custom'],
    'travel': ['3_months', '6_months', '9_months', '1_year', '18_months', 'custom'],
    'emergency_fund': ['6_months', '1_year', '18_months', '2_years', 'custom'],
    'business': ['1_year', '18_months', '2_years', '3_years', '4_years', '5_years', 'custom']
  };

  const goalTimeframeIds = goalTimeframes[goalId] || ['6_months', '1_year', '2_years', '3_years', 'custom'];
  
  return timeframeOptions.filter(option => 
    goalTimeframeIds.includes(option.value)
  );
};

// Función para calcular meses desde una fecha personalizada
export const calculateMonthsFromDate = (targetDate) => {
  const today = new Date();
  const target = new Date(targetDate);
  
  if (target <= today) return 1; // Mínimo 1 mes
  
  const yearDiff = target.getFullYear() - today.getFullYear();
  const monthDiff = target.getMonth() - today.getMonth();
  
  return (yearDiff * 12) + monthDiff;
};

// Función mejorada para calcular ahorro mensual
export const calculateMonthlySavings = (goalAmount, timeframe, customDate = null) => {
  let months;
  
  if (timeframe === 'custom' && customDate) {
    months = calculateMonthsFromDate(customDate);
  } else {
    const timeframeOption = timeframeOptions.find(option => option.value === timeframe);
    months = timeframeOption ? timeframeOption.months : 12;
  }
  
  return Math.ceil(goalAmount / months);
};

// Función para obtener recomendaciones de tiempo según el monto
export const getTimeRecommendations = (goalAmount, currency = 'USD') => {
  // Rangos de montos para recomendar tiempos (en USD como base)
  const exchangeRates = {
    'USD': 1,
    'EUR': 0.85,
    'CLP': 900,
    'MXN': 18,
    'ARS': 800,
    'COP': 4000
  };
  
  // Convertir a USD para la lógica
  const rate = exchangeRates[currency] || 1;
  const amountInUSD = goalAmount / rate;
  
  let recommendedCategory = 'medium';
  let recommendedTimeframes = [];
  
  if (amountInUSD < 5000) {
    recommendedCategory = 'short';
    recommendedTimeframes = ['3_months', '6_months', '1_year'];
  } else if (amountInUSD < 50000) {
    recommendedCategory = 'medium';
    recommendedTimeframes = ['1_year', '2_years', '3_years'];
  } else {
    recommendedCategory = 'long';
    recommendedTimeframes = ['3_years', '5_years', '7_years', '10_years'];
  }
  
  return {
    category: recommendedCategory,
    recommended: recommendedTimeframes,
    all: timeframeOptions.map(option => option.value)
  };
};

// Función para validar que una fecha personalizada sea razonable
export const validateCustomDate = (dateString, t) => {
  const date = new Date(dateString);
  const today = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() + 20); // Máximo 20 años

  if (isNaN(date.getTime())) {
    return { valid: false, error: t ? t('agtfin.utils.financial.validation.invalidDate') : 'Fecha inválida' };
  }

  if (date <= today) {
    return { valid: false, error: t ? t('agtfin.utils.financial.validation.futureDate') : 'La fecha debe ser futura' };
  }

  if (date > maxDate) {
    return { valid: false, error: t ? t('agtfin.utils.financial.validation.maxDate') : 'La fecha no puede ser mayor a 20 años' };
  }

  const months = calculateMonthsFromDate(dateString);
  if (months < 1) {
    return { valid: false, error: t ? t('agtfin.utils.financial.validation.minMonths') : 'Debe ser al menos un mes en el futuro' };
  }

  return { valid: true, months };
};

// Actualización de la configuración de metas con montos formateados
export const getGoalEstimatedCosts = (goalId, currency = 'USD') => {
  const costs = {
    buy_house: {
      USD: { min: 50000, max: 500000 },
      EUR: { min: 42500, max: 425000 },
      CLP: { min: 45000000, max: 450000000 },
      MXN: { min: 900000, max: 9000000 },
      ARS: { min: 40000000, max: 400000000 },
      COP: { min: 200000000, max: 2000000000 }
    },
    buy_car: {
      USD: { min: 15000, max: 80000 },
      EUR: { min: 12750, max: 68000 },
      CLP: { min: 13500000, max: 72000000 },
      MXN: { min: 270000, max: 1440000 },
      ARS: { min: 12000000, max: 64000000 },
      COP: { min: 60000000, max: 320000000 }
    },
    education: {
      USD: { min: 5000, max: 100000 },
      EUR: { min: 4250, max: 85000 },
      CLP: { min: 4500000, max: 90000000 },
      MXN: { min: 90000, max: 1800000 },
      ARS: { min: 4000000, max: 80000000 },
      COP: { min: 20000000, max: 400000000 }
    },
    travel: {
      USD: { min: 2000, max: 20000 },
      EUR: { min: 1700, max: 17000 },
      CLP: { min: 1800000, max: 18000000 },
      MXN: { min: 36000, max: 360000 },
      ARS: { min: 1600000, max: 16000000 },
      COP: { min: 8000000, max: 80000000 }
    },
    emergency_fund: {
      calculation: 'monthly_expenses * 3_to_6'
    },
    business: {
      USD: { min: 10000, max: 200000 },
      EUR: { min: 8500, max: 170000 },
      CLP: { min: 9000000, max: 180000000 },
      MXN: { min: 180000, max: 3600000 },
      ARS: { min: 8000000, max: 160000000 },
      COP: { min: 40000000, max: 800000000 }
    }
  };

  const goalCosts = costs[goalId];
  if (!goalCosts || !goalCosts[currency]) return null;

  const range = goalCosts[currency];
  return {
    min: formatCurrency(range.min, currency),
    max: formatCurrency(range.max, currency),
    minRaw: range.min,
    maxRaw: range.max
  };
};

// Función helper para obtener texto descriptivo del tiempo
export const getTimeframeDescription = (timeframe, customDate = null, t = null) => {
  if (timeframe === 'custom' && customDate) {
    const months = calculateMonthsFromDate(customDate);
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (t) {
      // Versión con traducciones
      if (years === 0) {
        return `${months} ${months === 1 ? t('agtfin.utils.financial.timeDescriptions.month') : t('agtfin.utils.financial.timeDescriptions.months')}`;
      } else if (remainingMonths === 0) {
        return `${years} ${years === 1 ? t('agtfin.utils.financial.timeDescriptions.year') : t('agtfin.utils.financial.timeDescriptions.years')}`;
      } else {
        return `${years} ${years === 1 ? t('agtfin.utils.financial.timeDescriptions.year') : t('agtfin.utils.financial.timeDescriptions.years')} ${t('agtfin.utils.financial.timeDescriptions.and')} ${remainingMonths} ${remainingMonths === 1 ? t('agtfin.utils.financial.timeDescriptions.month') : t('agtfin.utils.financial.timeDescriptions.months')}`;
      }
    } else {
      // Fallback sin traducciones
      if (years === 0) {
        return `${months} ${months === 1 ? 'mes' : 'meses'}`;
      } else if (remainingMonths === 0) {
        return `${years} ${years === 1 ? 'año' : 'años'}`;
      } else {
        return `${years} ${years === 1 ? 'año' : 'años'} y ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
      }
    }
  }

  if (t) {
    const option = getTimeframeOptions(t).find(opt => opt.value === timeframe);
    return option ? option.label : timeframe;
  } else {
    const option = timeframeOptions.find(opt => opt.value === timeframe);
    return option ? option.label : timeframe;
  }
};